// src/components/FullImageSlider.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";

/**
 * FullImageSlider
 * ✅ DB vide => fallback + autoplay actif
 * ✅ DB chargée => slides DB + autoplay actif
 * ✅ Si une slide DB n’a pas d’image => image statique de secours
 * ✅ Swipe mobile (gauche/droite)
 * ✅ Pause autoplay pendant interaction (hover / touch)
 * ✅ Support clavier (← →)
 */
export default function FullImageSlider({ slides = [], autoDelay = 4000 }) {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Touch swipe
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touching = useRef(false);

  const dbSlides = useMemo(() => {
    const arr = Array.isArray(slides) ? slides.filter(Boolean) : [];
    return arr;
  }, [slides]);

  const isDbReady = dbSlides.length > 0;

  // ✅ Images statiques fallback (mets tes fichiers dans /public/assets/)
  const staticImages = useMemo(
    () => [
      "./achetez.png",
      "/assets/hero-default-2.jpeg",
      "/assets/hero-default-1.jpeg",
    ],
    []
  );

  const fallbackSlides = useMemo(
    () => [
      {
        id: "fb-1",
        img: staticImages[0],
        backgroundColor: "#0b1226",
        href: "/catalog",
      },
      {
        id: "fb-2",
        img: staticImages[1],
        backgroundColor: "#0b1226",
        href: "/catalog",
      },
      {
        id: "fb-3",
        img: staticImages[2],
        backgroundColor: "#0b1226",
        href: "/catalog",
      },
    ],
    [staticImages]
  );

  // ✅ On affiche DB si dispo, sinon fallback
  const displaySlides = useMemo(() => {
    const source = isDbReady ? dbSlides : fallbackSlides;

    // Si une slide DB n’a pas d’image => on force une image statique
    return source.map((s, i) => {
      const imgSrc = s?.img || s?.imageUrl || s?.image || "";
      return {
        ...s,
        img: imgSrc || staticImages[i % staticImages.length],
      };
    });
  }, [isDbReady, dbSlides, fallbackSlides, staticImages]);

  // Reset index quand on passe fallback -> DB (plus propre)
  useEffect(() => {
    setIndex(0);
  }, [isDbReady]);

  const safeIndex =
    displaySlides.length > 0
      ? ((index % displaySlides.length) + displaySlides.length) %
        displaySlides.length
      : 0;

  const goTo = useCallback((i) => setIndex(i), []);
  const next = useCallback(() => setIndex((i) => i + 1), []);
  const prev = useCallback(() => setIndex((i) => i - 1), []);

  // ✅ Autoplay (pause quand interaction)
  useEffect(() => {
    if (isPaused) return;
    if (displaySlides.length <= 1) return;

    const id = setInterval(() => {
      setIndex((prev) => prev + 1);
    }, autoDelay);

    return () => clearInterval(id);
  }, [displaySlides.length, autoDelay, isPaused]);

  // ✅ Swipe handlers
  const onTouchStart = (e) => {
    touching.current = true;
    setIsPaused(true);
    touchStartX.current = e.touches?.[0]?.clientX || 0;
    touchEndX.current = touchStartX.current;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.touches?.[0]?.clientX || touchEndX.current;
  };

  const onTouchEnd = () => {
    const delta = touchStartX.current - touchEndX.current;

    touching.current = false;

    // seuil swipe
    if (Math.abs(delta) >= 50) {
      if (delta > 0) next(); // swipe gauche => next
      else prev(); // swipe droite => prev
    }

    // relance autoplay après un petit délai
    setTimeout(() => setIsPaused(false), 800);
  };

  // ✅ Support clavier (accessibilité)
  const onKeyDown = (e) => {
    if (displaySlides.length <= 1) return;
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  if (!displaySlides.length) return null;

  return (
    <section className="full-slider-wrapper my-3">
      <div
        className="full-slider"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        aria-roledescription="carousel"
        aria-label="Slider promotions"
      >
        {/* Flèches */}
        {displaySlides.length > 1 && (
          <>
            <button
              type="button"
              className="full-slider-arrow full-slider-arrow-left"
              onClick={prev}
              aria-label="Slide précédent"
            >
              ‹
            </button>
            <button
              type="button"
              className="full-slider-arrow full-slider-arrow-right"
              onClick={next}
              aria-label="Slide suivant"
            >
              ›
            </button>
          </>
        )}

        {displaySlides.map((s, i) => {
          const bg = s.backgroundColor || "#0b1226";
          const isActive = i === safeIndex;

          const Wrapper = ({ children }) => {
            if (!s.href) return children;

            const isExternal = /^https?:\/\//i.test(s.href);
            return isExternal ? (
              <a
                href={s.href}
                className="full-slide-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ) : (
              <Link to={s.href} className="full-slide-link">
                {children}
              </Link>
            );
          };

          return (
            <div
              key={s.id || i}
              className={"full-slide" + (isActive ? " full-slide-active" : "")}
              aria-hidden={!isActive}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} sur ${displaySlides.length}`}
            >
              <Wrapper>
                <div
                  className="full-slide-img-wrapper"
                  style={{ backgroundColor: bg }}
                >
                  <img
                    src={s.img}
                    alt={s.title || s.subtitle || "Slide"}
                    loading={i === safeIndex ? "eager" : "lazy"}
                    className="full-slide-img"
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.src =
                        staticImages[i % staticImages.length];
                    }}
                  />
                </div>
              </Wrapper>
            </div>
          );
        })}
      </div>

      {/* Points indicateurs */}
      {displaySlides.length > 1 && (
        <div className="full-slider-dots" aria-label="Navigation du slider">
          {displaySlides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={"full-slider-dot" + (i === safeIndex ? " active" : "")}
              onClick={() => goTo(i)}
              aria-label={`Aller au slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
